// src/hooks/useGizmoTitle.js
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { getMouseWorldPosition } from '../../../utils/getMouseWorldPosition';
import { throttleRAF } from '../../../utils/throttle';

function closestPointOnSegment(A, B, P) {
  const AP = new THREE.Vector3().subVectors(P, A);
  const AB = new THREE.Vector3().subVectors(B, A);
  const magnitudeAB = AB.lengthSq();
  const ABAPproduct = AP.dot(AB);
  const distance = ABAPproduct / magnitudeAB;
  if (distance < 0) return A.clone();
  else if (distance > 1) return B.clone();
  else return A.clone().add(AB.multiplyScalar(distance));
}

// Wire geometrisi vertex'lerini world-space'e dönüştürür
function getWorldSpaceVertices(wireObj) {
  if (!wireObj?.geometry?.attributes?.position) return null;
  const pos = wireObj.geometry.attributes.position;
  wireObj.updateMatrixWorld();
  const verts = [];
  for (let i = 0; i < pos.count; i++) {
    const v = new THREE.Vector3(pos.getX(i), pos.getY(i), pos.getZ(i));
    v.applyMatrix4(wireObj.matrixWorld);
    verts.push(v);
  }
  return verts;
}

// Tüm segment'ler içinden en yakın segment ve closest noktayı bulur
function findClosestSegment(vertices, point) {
  let minDist = Infinity, bestSeg = null, bestClosest = null;
  for (let i = 0; i < vertices.length - 1; i++) {
    const closest = closestPointOnSegment(vertices[i], vertices[i + 1], point);
    const dist = point.distanceTo(closest);
    if (dist < minDist) {
      minDist = dist;
      bestSeg = { p1: vertices[i], p2: vertices[i + 1] };
      bestClosest = closest;
    }
  }
  return { seg: bestSeg, closest: bestClosest };
}

// 🔧 wireTitle kilitleme durumu için global ref (export edilebilir)
export const wireTitleLockedRef = { current: false };
export const lockedWireTitleRef = { current: null };

export const useGizmoTitle = (scene, camera, renderer, selectedObjects) => {
  const isDragging = useRef(false);
  const selectedRef = useRef(null);
  const startInfo = useRef({});
  const lastMouse = useRef(new THREE.Vector2());
  const flipFactorRef = useRef(1);

  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const domElement = renderer.domElement;
    const raycaster = new THREE.Raycaster();
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

    const onMouseDown = (event) => {
      // 🔧 Eğer wireTitle kilitliyse, ikinci tıklamada kilidi çöz
      if (wireTitleLockedRef.current && lockedWireTitleRef.current) {
        wireTitleLockedRef.current = false;
        lockedWireTitleRef.current = null;
        isDragging.current = false;
        selectedRef.current = null;
        return;
      }
      const intersection = getMouseWorldPosition(event, domElement, camera);
      // Reconstruct ndc for compatibility
      const rect = domElement.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      const ndc = new THREE.Vector2(x, y);
      lastMouse.current.copy(ndc);
      flipFactorRef.current = 1;

      raycaster.setFromCamera(ndc, camera);
      const intersects = raycaster.intersectObjects(selectedObjects, true);

      if (intersects.length > 0) {
        let obj = intersects[0].object;
        while (obj && obj.type !== 'Scene') {
          // ✅ Panel title dragging (isDraggable: true, isTitle: true)
          if (obj.userData?.isDraggable && obj.userData?.isTitle && obj.userData?.titleType === 'panel') {
            selectedRef.current = obj;
            isDragging.current = true;

            const maxDistance = obj.userData.maxDistance || 110;

            // Get panel's world position (offsetGroup parent position)
            const panelWorldPos = new THREE.Vector3();
            if (obj.parent) {
              obj.parent.getWorldPosition(panelWorldPos);
            }

            // ✅ Get title's world position
            const titleWorldPos = new THREE.Vector3();
            obj.getWorldPosition(titleWorldPos);

            // ✅ Calculate mouse offset in world space
            const mouseOffset = titleWorldPos.clone().sub(intersection);

            startInfo.current = {
              titleType: 'panel',
              panelCenter: panelWorldPos.clone(),
              maxDistance: maxDistance,
              mouseOffset: mouseOffset
            };
            break;
          }

          // 🟡 Wire label (bundle menüsünden eklenen etiketler) - canlı geometri ile segment-kısıtlı sürükleme
          if (obj.userData?.type === 'wireLabel') {
            selectedRef.current = obj;
            isDragging.current = true;

            // forWireId üzerinden sahne'deki canlı bundle rep line'ı bul
            const forWireId = String(obj.userData?.forWireId || '');
            let wireObj = null;
            if (forWireId) {
              scene.traverse(o => {
                if (wireObj) return;
                if (o.isLine && (String(o.userData?.id) === forWireId || o.uuid === forWireId)) {
                  wireObj = o;
                }
              });
            }

            // World-space vertex'leri al (live geometri → tüm bundle uzunluğu)
            const vertices = (wireObj && getWorldSpaceVertices(wireObj)) || obj.userData.wireVertices || [];

            if (vertices.length >= 2) {
              const { seg, closest } = findClosestSegment(vertices, intersection);
              const dir = new THREE.Vector3().subVectors(seg.p2, seg.p1).normalize();
              const mouseOffset = intersection.clone().sub(closest);
              const alongComponent = mouseOffset.clone().projectOnVector(dir);
              const titleOffset = obj.position.clone().sub(closest.clone().add(alongComponent));
              startInfo.current = {
                titleType: 'wireLabel',
                wireObj,
                titleOffset,
              };
            } else {
              startInfo.current = { titleType: 'wireLabel', wireObj: null };
            }
            break;
          }

          // 🔵 Wire title - tıkla-kilitle davranışı
          if (obj.userData?.type === 'wireTitle') {
            selectedRef.current = obj;
            isDragging.current = true;

            // 🔧 Kilitleme durumunu aktifleştir
            wireTitleLockedRef.current = true;
            lockedWireTitleRef.current = obj;

            const vertices = obj.userData.wireVertices;
            let minDist = Infinity;
            let bestSegment = null;
            let closest = null;
            for (let i = 0; i < vertices.length - 1; i++) {
              const p1 = vertices[i];
              const p2 = vertices[i + 1];
              const candidate = closestPointOnSegment(p1, p2, intersection);
              const dist = intersection.distanceTo(candidate);
              if (dist < minDist) {
                minDist = dist;
                bestSegment = { p1, p2 };
                closest = candidate;
              }
            }

            if (closest && bestSegment) {
              const dir = new THREE.Vector3().subVectors(bestSegment.p2, bestSegment.p1).normalize();
              const perp = new THREE.Vector3(-dir.y, dir.x, 0);

              const mouseOffset = intersection.clone().sub(closest);
              const alongComponent = mouseOffset.clone().projectOnVector(dir);
              const offset = obj.position.clone().sub(closest.clone().add(alongComponent));
              const startStretchDistance = mouseOffset.dot(perp);

              const baseLength = obj.userData.lineLength || 30;
              startInfo.current = {
                titleType: 'wire',
                basePoint: closest,
                segment: bestSegment,
                titleOffset: offset,
                baseLength,
                startStretchDistance,
              };
            }
            break;
          }
          obj = obj.parent;
        }
      }
    };

    const onMouseMove = (event) => {
      // 🔧 Kilitli wireTitle varsa, isDragging olmasa bile takip et
      if (wireTitleLockedRef.current && lockedWireTitleRef.current) {
        selectedRef.current = lockedWireTitleRef.current;
        isDragging.current = true;
      }

      if (!isDragging.current || !selectedRef.current) return;
      const obj = selectedRef.current;
      const intersection = getMouseWorldPosition(event, domElement, camera);
      // Reconstruct ndc for compatibility
      const rect = domElement.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      const ndc = new THREE.Vector2(x, y);

      const dx = ndc.x - lastMouse.current.x;
      const dy = ndc.y - lastMouse.current.y;
      lastMouse.current.copy(ndc);

      // 🟡 Wire label dragging - canlı wire geometrisi, tüm bundle boyunca, wire'a dik
      if (startInfo.current.titleType === 'wireLabel') {
        const wireObj = startInfo.current.wireObj;
        // Her frame'de canlı world-space vertex'leri al (bundle güncellense de çalışır)
        const vertices = (wireObj && getWorldSpaceVertices(wireObj)) || obj.userData.wireVertices || [];
        if (vertices.length < 2) return;

        const { seg, closest: closestOnWire } = findClosestSegment(vertices, intersection);
        if (!seg) return;

        const dir = new THREE.Vector3().subVectors(seg.p2, seg.p1).normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0);

        const offsetAlong = startInfo.current.titleOffset.clone().projectOnVector(dir);
        obj.position.copy(closestOnWire.clone().add(offsetAlong));

        const mouseOffsetFromWire = intersection.clone().sub(closestOnWire);
        const sideOfWire = mouseOffsetFromWire.dot(perp);
        const autoFlip = sideOfWire < 0 ? -1 : 1;
        const stretched = THREE.MathUtils.clamp(Math.abs(sideOfWire), 0, 150);

        obj.children.forEach((child) => {
          if (child.name === 'titleLine') {
            child.geometry.dispose();
            child.geometry = new THREE.BufferGeometry().setFromPoints([
              new THREE.Vector3(0, stretched, 0),
              new THREE.Vector3(0, 0, 0),
            ]);
          }
          if (child.name === 'titleLabel' || child.name === 'titleInnerCircle') {
            child.position.set(0, stretched + 10, 0);
          }
          if (child.name === 'titleCircle') {
            const newShape = new THREE.Shape();
            newShape.absarc(0, stretched + 10, 10, 0, Math.PI * 2, false);
            child.geometry.dispose();
            child.geometry = new THREE.BufferGeometry().setFromPoints(newShape.getPoints(64));
            child.computeLineDistances();
          }
        });

        obj.rotation.z = Math.atan2(perp.y, perp.x) - Math.PI / 2 + (autoFlip === -1 ? Math.PI : 0);
        obj.userData.lineLength = stretched;
        return;
      }

      // ✅ Panel title dragging - simple constrained movement
      if (startInfo.current.titleType === 'panel') {
        const panelCenter = startInfo.current.panelCenter;
        const maxDistance = startInfo.current.maxDistance;
        const mouseOffset = startInfo.current.mouseOffset;

        // Calculate desired world position (mouse + offset)
        const desiredWorldPos = intersection.clone().add(mouseOffset);

        // Constrain to maxDistance from panel center
        const offset = desiredWorldPos.clone().sub(panelCenter);
        const distance = offset.length();

        if (distance > maxDistance) {
          offset.normalize().multiplyScalar(maxDistance);
          desiredWorldPos.copy(panelCenter.clone().add(offset));
        }

        // Convert world position to local position
        if (obj.parent) {
          const parentWorldMatrix = new THREE.Matrix4();
          obj.parent.updateMatrixWorld();
          parentWorldMatrix.copy(obj.parent.matrixWorld).invert();
          const localPos = desiredWorldPos.clone().applyMatrix4(parentWorldMatrix);
          obj.position.copy(localPos);
        } else {
          obj.position.copy(desiredWorldPos);
        }

        return;
      }

      // 🔵 Wire title dragging - complex segment-based movement
      if (startInfo.current.titleType === 'wire') {
        const vertices = obj.userData.wireVertices;
        let minDist = Infinity;
        let bestSegment = null;
        let closest = null;

        for (let i = 0; i < vertices.length - 1; i++) {
          const p1 = vertices[i];
          const p2 = vertices[i + 1];
          const candidate = closestPointOnSegment(p1, p2, intersection);
          const dist = intersection.distanceTo(candidate);
          if (dist < minDist) {
            minDist = dist;
            bestSegment = { p1, p2 };
            closest = candidate;
          }
        }

        const dir = new THREE.Vector3().subVectors(bestSegment.p2, bestSegment.p1).normalize();
        const perp = new THREE.Vector3(-dir.y, dir.x, 0);

        const offsetAlong = startInfo.current.titleOffset.clone().projectOnVector(dir);

        const projected = intersection.clone().sub(bestSegment.p1);
        const t = THREE.MathUtils.clamp(projected.dot(dir), 0, bestSegment.p2.clone().sub(bestSegment.p1).length());
        const closestOnWire = bestSegment.p1.clone().add(dir.clone().multiplyScalar(t));
        const basePos = closestOnWire.clone().add(offsetAlong);

        obj.position.copy(basePos);

        // 🔧 Mouse wire'ın hangi tarafında? Otomatik flip
        const mouseOffsetFromWire = intersection.clone().sub(closestOnWire);
        const sideOfWire = mouseOffsetFromWire.dot(perp);

        // Mouse wire'ın negatif tarafındaysa flip yap
        const autoFlip = sideOfWire < 0 ? -1 : 1;

        // Mouse'un wire'a olan uzaklığını kullanarak stretch hesapla
        const distanceFromWire = Math.abs(sideOfWire);
        const stretched = THREE.MathUtils.clamp(distanceFromWire, 0, 150);

        obj.children.forEach((child) => {
          if (child.name === 'titleLine') {
            child.geometry.dispose();
            child.geometry = new THREE.BufferGeometry().setFromPoints([
              new THREE.Vector3(0, stretched, 0),
              new THREE.Vector3(0, 0, 0),
            ]);
          }
          if (child.name === 'titleLabel' || child.name === 'titleInnerCircle') {
            child.position.set(0, stretched + 10, 0);
          }
          if (child.name === 'titleCircle') {
            const newShape = new THREE.Shape();
            newShape.absarc(0, stretched + 10, 10, 0, Math.PI * 2, false);
            child.geometry.dispose();
            child.geometry = new THREE.BufferGeometry().setFromPoints(newShape.getPoints(64));
            child.computeLineDistances();
          }
        });

        // 🔧 Rotasyonu mouse'un bulunduğu tarafa göre ayarla
        obj.rotation.z = Math.atan2(perp.y, perp.x) - Math.PI / 2 + (autoFlip === -1 ? Math.PI : 0);
        obj.userData.lineLength = stretched;
      }
    };

    const onMouseUp = () => {
      // 🔧 wireTitle kilitliyse, mouse up'ta bırakma - ikinci tıklamayı bekle
      if (wireTitleLockedRef.current && lockedWireTitleRef.current) {
        return; // Kilidi korumaya devam et
      }

      isDragging.current = false;
      selectedRef.current = null;
    };
    const onKeyDown = (event) => {
      // 🔧 ESC ile wireTitle kilidini iptal et
      if (event.key === 'Escape') {
        if (wireTitleLockedRef.current) {
          wireTitleLockedRef.current = false;
          lockedWireTitleRef.current = null;
          isDragging.current = false;
          selectedRef.current = null;
        }
      }

      if (event.key === 'r' || event.key === 'R') {
        if (isDragging.current) {
          flipFactorRef.current *= -1;
        }
      }
    };
    const throttledMouseMove = throttleRAF(onMouseMove);

    domElement.addEventListener('mousedown', onMouseDown);
    domElement.addEventListener('mousemove', throttledMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      throttledMouseMove.cancel();
      domElement.removeEventListener('mousedown', onMouseDown);
      domElement.removeEventListener('mousemove', throttledMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [scene, camera, renderer, selectedObjects]);
};